import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Snowflake {
  id: string;
  x: number;
  size: number;
  duration: number;
  delay: number;
}

export function SnowGenerator({ active }: { active: boolean }) {
  const [snowflakes, setSnowflakes] = useState<Snowflake[]>([]);

  const generateSnowflake = useCallback((): Snowflake => ({
    id: Math.random().toString(36).substring(7),
    x: Math.random() * 100,
    size: Math.random() * 4 + 2,
    duration: Math.random() * 3 + 2,
    delay: Math.random() * 0.5,
  }), []);

  const removeSnowflake = useCallback((id: string) => {
    setSnowflakes((prev) => prev.filter((flake) => flake.id !== id));
  }, []);

  useEffect(() => {
    if (!active) {
      setSnowflakes([]);
      return;
    }

    // Generate initial snowflakes
    const initial = Array.from({ length: 30 }, generateSnowflake);
    setSnowflakes(initial);

    // Add new snowflakes periodically
    const interval = setInterval(() => {
      if (active) {
        setSnowflakes((prev) => {
          if (prev.length >= 60) return prev;
          return [...prev, generateSnowflake()];
        });
      }
    }, 300);

    return () => clearInterval(interval);
  }, [active, generateSnowflake]);

  if (!active && snowflakes.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <AnimatePresence mode="popLayout">
        {snowflakes.map((flake) => (
          <motion.div
            key={flake.id}
            initial={{ y: -20, opacity: 0 }}
            animate={{
              y: typeof window !== 'undefined' ? window.innerHeight + 20 : 1000,
              opacity: [0, 0.8, 0.6, 0],
            }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            transition={{
              duration: flake.duration,
              delay: flake.delay,
              ease: 'linear',
            }}
            onAnimationComplete={() => {
              if (active) {
                // Recycle snowflake with new properties
                removeSnowflake(flake.id);
              }
            }}
            className="absolute rounded-full bg-white shadow-sm"
            style={{
              left: `${flake.x}%`,
              width: `${flake.size}px`,
              height: `${flake.size}px`,
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.5)',
            }}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
